/**
 * Batch 27h — Super-admin Tenants list (rewrite).
 *
 * Filter (status), search (name/slug ILIKE), sort, paginate. Each row
 * displays at-a-glance counts: users, plan, credits.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/admin/PageHeader";
import { TenantsFilters } from "@/components/admin/tenants/TenantsFilters";
import { TenantsPager } from "@/components/admin/tenants/TenantsPager";
import {
  useAdminTenants,
  type TenantSortKey,
  type TenantStatusFilter,
} from "@/hooks/queries/admin/useAdminTenants";

const CTA_GRADIENT =
  "bg-gradient-to-r from-brand-600 to-[var(--p-teal-600)] text-white shadow-sm hover:shadow-md hover:brightness-110 transition";

const PAGE_SIZE = 50;

function StatusPill({ status }: { status: string }): JSX.Element {
  const tone =
    status === "active"
      ? "bg-brand-positive/10 text-[var(--p-green-700)] ring-brand-positive/30 dark:bg-brand-positive/15 dark:text-[var(--p-green-350)]"
      : status === "suspended"
        ? "bg-yellow-100 text-yellow-800 ring-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/30"
        : status === "pending_deletion"
          ? "bg-red-100 text-red-800 ring-red-300 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30"
          : "bg-brand-100 text-[var(--p-ink-700)] ring-[var(--p-border-input-light)] dark:bg-white/[0.06] dark:text-brand-100/60 dark:ring-white/[0.08]";
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ring-1 ${tone}`}
    >
      {status}
    </span>
  );
}

export default function AdminTenants(): JSX.Element {
  const [status, setStatus] = useState<TenantStatusFilter>("all");
  const [sort, setSort] = useState<TenantSortKey>("created_at_desc");
  const [qInput, setQInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [offset, setOffset] = useState(0);

  // 300ms debounce on the search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(qInput), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Reset offset when filter/sort/search changes.
  useEffect(() => {
    setOffset(0);
  }, [status, sort, debouncedQ]);

  const queryParams = useMemo(
    () => ({ status, sort, q: debouncedQ, limit: PAGE_SIZE, offset }),
    [status, sort, debouncedQ, offset],
  );
  const { data, isLoading, error } = useAdminTenants(queryParams);

  const tenants = data?.tenants ?? [];
  const totalCount = data?.total_count ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <PageHeader title="Tenants" />
        <Link
          to="/tenants/new"
          className={`rounded-md px-3 py-1.5 text-sm font-medium ${CTA_GRADIENT}`}
        >
          Provision tenant
        </Link>
      </div>

      <TenantsFilters
        status={status}
        sort={sort}
        q={qInput}
        onStatus={setStatus}
        onSort={setSort}
        onQuery={setQInput}
      />

      {error ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Couldn't load tenants: {error.message}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-[var(--p-slate-150)] bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Slug</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Plan</th>
              <th className="px-4 py-2 font-medium">Credits</th>
              <th className="px-4 py-2 font-medium">Users</th>
              <th className="px-4 py-2 font-medium">Trial ends</th>
              <th className="px-4 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && tenants.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  Loading tenants…
                </td>
              </tr>
            ) : tenants.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  No tenants match the current filter.
                </td>
              </tr>
            ) : (
              tenants.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-brand-100 transition-colors hover:bg-brand-50 dark:border-white/[0.04] dark:hover:bg-white/[0.03]"
                >
                  <td className="px-4 py-2 font-medium text-text-primary dark:text-text-primary">
                    <Link
                      to={`/tenants/${t.id}`}
                      className="hover:text-accent-primary-hover dark:hover:text-accent-secondary"
                    >
                      {t.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {t.slug}
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill status={t.status} />
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {t.plan_name ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {t.current_credits.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {t.user_count}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {t.trial_ends_at
                      ? new Date(t.trial_ends_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TenantsPager
        totalCount={totalCount}
        limit={PAGE_SIZE}
        offset={offset}
        onOffset={setOffset}
      />
    </div>
  );
}
